<template>
  <div class="partners-logos">
    <!-- Gold Partners -->
    <div v-if="goldLogos.length > 0" class="mb-12">
      <div class="partners-row partners-row-gold">
        <div 
          v-for="logo in goldLogos" 
          :key="logo.name"
          class="partner-logo-container gold"
          @click="openModal(logo)"
        >
          <img 
            :src="logo.url" 
            :alt="logo.name"
            class="partner-logo gold"
            @error="handleImageError"
          />
        </div>
      </div>
    </div>

    <!-- Silver Partners -->
    <div v-if="silverLogos.length > 0" class="mb-10">
      <div class="partners-row partners-row-silver">
        <div 
          v-for="logo in silverLogos" 
          :key="logo.name"
          class="partner-logo-container silver"
          @click="openModal(logo)"
        >
          <img 
            :src="logo.url" 
            :alt="logo.name"
            class="partner-logo silver"
            @error="handleImageError"
          />
        </div>
      </div>
    </div>

    <!-- Classic Partners -->
    <div v-if="classicLogos.length > 0" class="mb-8">
      <div class="partners-row partners-row-classic">
        <div 
          v-for="logo in classicLogos" 
          :key="logo.name"
          class="partner-logo-container classic"
          @click="openModal(logo)"
        >
          <img 
            :src="logo.url" 
            :alt="logo.name"
            class="partner-logo classic"
            @error="handleImageError"
          />
        </div>
      </div>
    </div>
    
    <!-- Modal de prévisualisation -->
    <div 
      v-if="selectedLogo" 
      class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 modal-backdrop"
      @click="closeModal"
    >
      <div class="max-w-4xl max-h-full bg-white rounded-lg overflow-hidden modal-content" @click.stop>
        <div class="relative">
          <!-- Image en grand : même crop que les vignettes (contenu du rectangle blanc uniquement) -->
          <div class="p-8 modal-logo-crop">
            <img 
              :src="selectedLogo.url" 
              :alt="selectedLogo.name"
              class="max-w-full max-h-96 mx-auto object-contain modal-logo-img"
            />
          </div>
          
          <!-- Bouton de fermeture -->
          <button 
            @click="closeModal"
            class="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PartnersLogos',
  data() {
    return {
      goldLogos: [],
      silverLogos: [],
      classicLogos: [],
      selectedLogo: null
    }
  },
  async mounted() {
    await this.loadLogos()
  },
  methods: {
    async loadLogos() {
      try {
        const manifest = await this.getPartnersManifest()

        this.goldLogos = this.mapLogoFiles('gold', manifest.gold || [])
        this.silverLogos = this.mapLogoFiles('silver', manifest.silver || [])
        this.classicLogos = this.mapLogoFiles('classic', manifest.classic || [])
      } catch (error) {
        console.error('Erreur lors du chargement des logos:', error)
      }
    },

    async getPartnersManifest() {
      const response = await fetch('/assets/partners/manifest.json')

      if (!response.ok) {
        throw new Error(`Impossible de charger le manifest des partenaires (${response.status})`)
      }

      return response.json()
    },

    mapLogoFiles(category, files) {
      return files.map(file => ({
        name: this.getLogoName(file),
        url: `/assets/partners/${category}/${file}`
      }))
    },

    getLogoName(filename) {
      return filename
        .replace(/[_-]/g, ' ')
        .replace(/\.(jpg|jpeg|png|gif|webp|svg)$/i, '')
        .replace(/\b\w/g, l => l.toUpperCase())
    },

    handleImageError(event) {
      console.error('Erreur de chargement de l\'image:', event.target.src)
      // Optionnel : masquer l'image en cas d'erreur
      event.target.style.display = 'none'
    },
    
    openModal(logo) {
      this.selectedLogo = logo
    },
    
    closeModal() {
      this.selectedLogo = null
    }
  }
}
</script>

<style scoped>
/* Ajustez ces valeurs pour le crop du rectangle blanc (top right bottom left) */
.partners-logos {
  --crop-top: 14%;
  --crop-right: 6%;
  --crop-bottom: 22%;
  --crop-left: 6%;
  --crop-radius: 10px;
  --gold-width: 320px;
  --gold-height: 160px;
  --silver-width: 240px;
  --silver-height: 120px;
  --classic-width: 180px;
  --classic-height: 96px;
  width: 100%;
}

.partners-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.partners-row-gold {
  gap: 2rem 2.5rem;
}

.partners-row-silver {
  gap: 1.5rem 2rem;
}

.partners-row-classic {
  gap: 1.25rem 1.5rem;
}

.partner-logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden; /* Cache les parties qui dépassent */
  cursor: pointer; /* Indique que c'est cliquable */
}

.partner-logo-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.partner-logo-container.gold {
  padding: 0px;
  min-height: var(--gold-height);
  width: var(--gold-width);
  height: var(--gold-height);
  flex-shrink: 0;
}

.partner-logo-container.silver {
  padding: 0px;
  min-height: var(--silver-height);
  width: var(--silver-width);
  height: var(--silver-height);
  flex-shrink: 0;
}

.partner-logo-container.classic {
  padding: 0px;
  min-height: var(--classic-height);
  width: var(--classic-width);
  height: var(--classic-height);
  flex-shrink: 0;
}

.partner-logo {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Recadre l'image pour remplir le conteneur */
  /* Crop automatique : n'afficher que le contenu du rectangle blanc arrondi (cadre exclu) */
  clip-path: inset(var(--crop-top) var(--crop-right) var(--crop-bottom) var(--crop-left) round var(--crop-radius));
  -webkit-clip-path: inset(var(--crop-top) var(--crop-right) var(--crop-bottom) var(--crop-left) round var(--crop-radius));
  filter: grayscale(20%);
  transition: filter 0.2s ease;
}

.partner-logo.gold {
  object-position: center 30%; /* Ajuste la position verticale pour les Gold */
}

.partner-logo.silver {
  object-position: center 35%;
}

.partner-logo.classic {
  object-position: center 40%;
}

.partner-logo:hover {
  filter: grayscale(0%);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .partners-logos {
    --gold-width: 240px;
    --gold-height: 128px;
    --silver-width: 180px;
    --silver-height: 96px;
    --classic-width: 135px;
    --classic-height: 72px;
  }

  .partners-row-gold {
    gap: 1.5rem 1.75rem;
  }

  .partners-row-silver {
    gap: 1.25rem 1.5rem;
  }

  .partners-row-classic {
    gap: 1rem 1.25rem;
  }

  .partner-logo-container.gold {
    padding: 12px;
  }
  
  .partner-logo-container.silver,
  .partner-logo-container.classic {
    padding: 6px;
  }
  
  .partner-logo.gold {
    object-position: center 25%;
  }
  
  .partner-logo.silver {
    object-position: center 30%;
  }

  .partner-logo.classic {
    object-position: center 35%;
  }
}

@media (max-width: 480px) {
  .partners-logos {
    --gold-width: 200px;
    --gold-height: 112px;
    --silver-width: 155px;
    --silver-height: 84px;
    --classic-width: 115px;
    --classic-height: 64px;
  }

  .partners-row-gold {
    gap: 1.25rem 1.5rem;
  }

  .partner-logo-container.gold {
    padding: 10px;
  }
  
  .partner-logo-container.silver,
  .partner-logo-container.classic {
    padding: 5px;
  }
  
  .partner-logo.gold {
    object-position: center 20%;
  }
  
  .partner-logo.silver {
    object-position: center 25%;
  }

  .partner-logo.classic {
    object-position: center 30%;
  }
}

/* Modal : crop pour n'afficher que le contenu du rectangle blanc */
.modal-logo-crop {
  display: flex;
  justify-content: center;
  align-items: center;
}
.modal-logo-img {
  /* Mêmes proportions de crop que les vignettes (cadre, fond, logo bas exclus) */
  clip-path: inset(var(--crop-top) var(--crop-right) var(--crop-bottom) var(--crop-left) round 12px);
  -webkit-clip-path: inset(var(--crop-top) var(--crop-right) var(--crop-bottom) var(--crop-left) round 12px);
}

/* Modal styles */
.modal-backdrop {
  backdrop-filter: blur(4px);
}

.modal-content {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
